import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  LogOut,
  Menu,
  X,
  Bell,
  Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { authorAPI, notificationsAPI } from '../../lib/api';
import { JabalipuraanLogo } from '../../components/common/JabalipuraanLogo';

const sidebarLinks = [
  { href: '/author', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { href: '/author/articles', icon: FileText, label: 'My Articles' },
];

export const AuthorLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Authors AND Admins can access the author panel
  const canAccessAuthorPanel = user?.role === 'author' || user?.role === 'admin';

  useEffect(() => {
    if (!loading && (!user || !canAccessAuthorPanel)) {
      navigate('/login');
    }
  }, [user, canAccessAuthorPanel, loading, navigate]);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationsAPI.getUnreadCount();
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
      }
    };
    if (canAccessAuthorPanel) {
      fetchUnreadCount();
      // Poll every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [canAccessAuthorPanel]);

  const isActive = (href, exact = false) => {
    if (exact) return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user || !canAccessAuthorPanel) {
    return null;
  }

  const displayRole = user.role === 'admin' ? 'Admin / Author' : 'Author';

  return (
    <div className="min-h-screen bg-muted/30 flex" data-testid="author-layout">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-primary text-primary-foreground transform transition-transform duration-300 lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-primary-foreground/10">
            <div className="flex items-center gap-3">
              <JabalipuraanLogo size="sm" linkTo="/" />
              <div>
                <span className="font-heading text-lg font-semibold text-primary-foreground block">
                  Jabalipuraan
                </span>
                <span className="text-xs text-primary-foreground/60">Author Panel</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {sidebarLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center px-3 py-2 rounded-md transition-colors ${
                  isActive(link.href, link.exact) 
                    ? 'bg-primary-foreground/20 text-primary-foreground' 
                    : 'text-primary-foreground/70 hover:bg-primary-foreground/10 hover:text-primary-foreground'
                }`}
                data-testid={`author-nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                <link.icon className="h-5 w-5 mr-3" />
                {link.label}
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-primary-foreground/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
                <span className="text-accent font-semibold">
                  {user.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-primary-foreground">{user.name}</p>
                <p className="text-xs text-primary-foreground/60">{displayRole}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10"
              onClick={() => { logout(); navigate('/'); }}
              data-testid="author-logout"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-background border-b border-border px-4 py-3 flex items-center justify-between">
          <button
            className="lg:hidden p-2"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="author-mobile-menu"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <Link to="/author/notifications" className="relative p-2 hover:bg-muted rounded-md">
              <Bell className="h-5 w-5 text-muted-foreground" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Link>
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
              View Site →
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

// Author Dashboard Overview Component
export default function AuthorDashboard() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await authorAPI.getArticles();
        setArticles(response.data);
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  const stats = {
    draft: articles.filter(a => a.status === 'draft').length,
    pending: articles.filter(a => a.status === 'pending').length,
    approved: articles.filter(a => a.status === 'approved').length,
    rejected: articles.filter(a => a.status === 'rejected').length,
  };

  const statCards = [
    { label: 'Drafts', value: stats.draft, color: 'bg-gray-100 text-gray-800' },
    { label: 'Pending Review', value: stats.pending, color: 'bg-yellow-100 text-yellow-800' },
    { label: 'Published', value: stats.approved, color: 'bg-green-100 text-green-800' },
    { label: 'Needs Revision', value: stats.rejected, color: 'bg-red-100 text-red-800' },
  ];

  return (
    <div data-testid="author-dashboard">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-foreground mb-2">
          Author Dashboard
        </h1>
        <p className="text-muted-foreground">
          Write and manage your heritage articles.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, idx) => (
          <div
            key={idx}
            className="bg-background p-6 border border-border"
          >
            <div className="flex items-center justify-between mb-2">
              {loading ? (
                <div className="h-8 w-16 bg-muted animate-pulse"></div>
              ) : (
                <span className="text-3xl font-bold text-foreground">{stat.value}</span>
              )}
            </div>
            <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${stat.color}`}>
              {stat.label}
            </span>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-background p-6 border border-border mb-8">
        <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
          Quick Actions
        </h2>
        <div className="flex gap-4">
          <Link to="/author/articles/new">
            <Button data-testid="quick-new-article">
              <Plus className="h-4 w-4 mr-2" />
              Write New Article
            </Button>
          </Link>
          <Link to="/author/articles">
            <Button variant="outline">
              View My Articles
            </Button>
          </Link>
        </div>
      </div>

      {/* Recent Articles */}
      {articles.length > 0 && (
        <div className="bg-background p-6 border border-border">
          <h2 className="font-heading text-xl font-semibold text-foreground mb-4">
            Recent Articles
          </h2>
          <div className="space-y-3">
            {articles.slice(0, 5).map((article) => (
              <Link
                key={article.id}
                to={`/author/articles/${article.id}/edit`}
                className="flex items-center justify-between p-3 bg-muted/30 rounded hover:bg-muted/50 transition-colors"
              >
                <div>
                  <h3 className="font-medium text-foreground">{article.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {new Date(article.updated_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded ${
                  article.status === 'approved' ? 'bg-green-100 text-green-800' :
                  article.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  article.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {article.status === 'approved' ? 'Published' :
                   article.status === 'pending' ? 'Pending' :
                   article.status === 'rejected' ? 'Needs Revision' :
                   'Draft'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
